/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FollowMeTab.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Follow Me tab for Extension form - Configure Follow Me destinations
────────────────────────────────────────────────────────────────*/

import { Phone, Plus, Trash2, GripVertical } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../hooks/useI18n';
import { v4 as uuidv4 } from 'uuid';
import type { FollowMeSettingsState } from '../types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface FollowMeTabProps {
  followMeData: FollowMeSettingsState;
  onChange: (field: string, value: unknown) => void;
  onFollowMeChange: (field: string, value: unknown) => void;
}

export function FollowMeTab({
  followMeData,
  onChange,
  onFollowMeChange,
}: FollowMeTabProps) {
  const { t } = useI18n();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleAddDestination = () => {
    const newDestination = {
      id: uuidv4(),
      destination: '',
      delay: 0,
      timeout: 30,
      prompt: '',
    };
    onFollowMeChange('destinations', [...followMeData.destinations, newDestination]);
  };

  const handleRemoveDestination = (id: string) => {
    onFollowMeChange('destinations', followMeData.destinations.filter(d => d.id !== id));
  };

  const handleDestinationChange = (id: string, field: string, value: unknown) => {
    onFollowMeChange('destinations', followMeData.destinations.map(d =>
      d.id === id ? { ...d, [field]: value } : d
    ));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = followMeData.destinations.findIndex(d => d.id === active.id);
      const newIndex = followMeData.destinations.findIndex(d => d.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const updated = arrayMove(followMeData.destinations, oldIndex, newIndex);
      onFollowMeChange('destinations', updated);
    }
  };

  return (
    <div className="space-y-4">
      {/* Follow Me Section */}
      <FormBox
        title={t('extensions.followMe.title', 'Follow Me')}
        icon={<Phone className="h-5 w-5" />}
      >
        <div className="space-y-2">
          {/* Enable Follow Me and Ignore Busy in the same line */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 items-start">
            <SimpleTooltip
              content={t('extensions.followMe.followMe.tooltip', 'Enable Follow Me to ring multiple destinations.')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left font-medium">
                {t('extensions.followMe.followMe.label', 'Follow Me')}
              </Label>
            </SimpleTooltip>
            <Toggle
              value={followMeData.enabled}
              onChange={(value) => onFollowMeChange('enabled', value)}
            />
            {followMeData.enabled ? (
              <>
                <SimpleTooltip
                  content={t('extensions.followMe.ignoreBusy.tooltip', 'Interrupt the call if a destination is busy.')}
                  position="top"
                  maxWidth="300px"
                >
                  <Label className="text-left font-medium">
                    {t('extensions.followMe.ignoreBusy.label', 'Ignore Busy')}
                  </Label>
                </SimpleTooltip>
                <Toggle
                  value={followMeData.ignoreBusy}
                  onChange={(value) => onFollowMeChange('ignoreBusy', value)}
                />
              </>
            ) : (
              <div className="col-span-2"></div>
            )}
          </div>

          {/* Destinations Table */}
          {followMeData.enabled && (
            <div className="mt-4">
              {followMeData.destinations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-md">
                  <p className="text-sm">
                    {t('extensions.followMe.destinations.empty', 'No destinations configured')}
                  </p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <div className="border border-border rounded-md overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-foreground w-10">
                            <GripVertical className="h-4 w-4 opacity-0" />
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-foreground">
                            {t('extensions.followMe.destinations.destination', 'Destination')}
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-foreground">
                            {t('extensions.followMe.destinations.delay', 'Delay')}
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-foreground">
                            {t('extensions.followMe.destinations.timeout', 'Timeout')}
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-foreground">
                            {t('extensions.followMe.destinations.prompt', 'Prompt')}
                          </th>
                          <th className="px-4 py-2 text-center text-sm font-medium text-foreground w-12">
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <SortableContext
                          items={followMeData.destinations.map(d => d.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {followMeData.destinations.map((dest) => (
                            <DestinationRowItem
                              key={dest.id}
                              dest={dest}
                              onDestinationChange={handleDestinationChange}
                              onRemove={handleRemoveDestination}
                              t={t}
                            />
                          ))}
                        </SortableContext>
                      </tbody>
                    </table>
                  </div>
                </DndContext>
              )}
              {/* Add Button - aligned right */}
              <div className="flex justify-end mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddDestination}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {t('extensions.followMe.destinations.addShort', 'Add')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </FormBox>
    </div>
  );
}

interface DestinationRowItemProps {
  dest: {
    id: string;
    destination: string;
    delay: number;
    timeout: number;
    prompt: string;
  };
  onDestinationChange: (id: string, field: string, value: unknown) => void;
  onRemove: (id: string) => void;
  t: (key: string, defaultValue?: string) => string;
}

function DestinationRowItem({ dest, onDestinationChange, onRemove, t }: DestinationRowItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: dest.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-t border-border hover:bg-muted/30 ${isDragging ? 'shadow-lg ring-2 ring-primary/40 bg-card' : ''}`}
    >
      <td className="px-4 py-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted/60 rounded"
          aria-label={t('extensions.followMe.destinations.reorder', 'Reorder destination')}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </td>
      <td className="px-4 py-2">
        <Input
          value={dest.destination}
          onChange={(e) => onDestinationChange(dest.id, 'destination', e.target.value)}
          placeholder={t('extensions.followMe.destinations.destinationPlaceholder', 'e.g., 1001, +1234567890')}
          className="w-full"
        />
      </td>
      <td className="px-4 py-2">
        <select
          value={dest.delay}
          onChange={(e) => onDestinationChange(dest.id, 'delay', parseInt(e.target.value))}
          className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {Array.from({ length: 61 }, (_, i) => i).map(num => (
            <option key={num} value={num}>{num}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        <select
          value={dest.timeout}
          onChange={(e) => onDestinationChange(dest.id, 'timeout', parseInt(e.target.value))}
          className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 90, 120].map(num => (
            <option key={num} value={num}>{num}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        <select
          value={dest.prompt}
          onChange={(e) => onDestinationChange(dest.id, 'prompt', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">{t('extensions.followMe.destinations.promptNone', 'None')}</option>
          <option value="caller-id">{t('extensions.followMe.destinations.promptCallerId', 'Caller ID')}</option>
          <option value="position">{t('extensions.followMe.destinations.promptPosition', 'Position')}</option>
          <option value="both">{t('extensions.followMe.destinations.promptBoth', 'Both')}</option>
        </select>
      </td>
      <td className="px-4 py-2 text-center">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(dest.id)}
          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}
